// Számla-kiállítási akciók megerősítő-dialógusai + a vezérlő hook — a LISTA és a DETAIL közös forrása.
// A megerősítő-minta (ConfirmDialog): kiállítás (warning), sztornó (danger + opcionális megjegyzés-mező),
// díjbekérő-érvénytelenítés (danger). A mutációk + a PDF-letöltés a `useInvoiceLifecycle` hookból jönnek.
import { shiftIssueDates, type InvoiceDates } from '@atlas/shared';
import { type TFunction } from 'i18next';
import { useState } from 'react';

import { useInvoiceLifecycle } from '../lifecycle';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DatePicker } from '@/components/ui/date-picker';
import { Textarea } from '@/components/ui/textarea';

/** A státusz-műveletekhez szükséges minimális számla-alak (a lista-sor ÉS a detail is ilyet ad). */
export interface InvoiceActionRow {
  id: string;
  version: number;
  status: string;
  type: string | null;
  invoiceNumber: string | null;
  stornoInvoiceNumber: string | null;
  /**
   * A piszkozat három dátuma — a KIÁLLÍTÁS ablaka ezekből számolja az előnézetet (`shiftIssueDates`).
   * A többi művelet (sztornó, érvénytelenítés) nem használja; hiányuk nem töri a dialógust.
   */
  issueDate?: string | null | undefined;
  fulfillmentDate?: string | null | undefined;
  dueDate?: string | null | undefined;
}

type ActionKind = 'issue' | 'storno' | 'cancel';

const EMPTY_DATES: InvoiceDates = { issueDate: '', fulfillmentDate: '', dueDate: '' };

/** A MAI nap `Europe/Budapest` szerint (`YYYY-MM-DD`) — a bizonylat kelte naptári nap, nem UTC-pillanat. */
function todayIso(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Budapest',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/**
 * A számla-akciók vezérlője: a megnyitott dialógus + a cél-sor + a sztornó-megjegyzés állapota, valamint a
 * mutációk. A `onDone` (siker után) a hívó cache-invalidálásán túli extra (pl. detail-navigáció) — a
 * cache-frissítést a hook maga végzi. A visszaadott `openIssue/openStorno/openCancel/downloadPdf`-et a
 * trigger-gombok hívják; a dialógusokat a `<InvoiceActionDialogs controller=… />` rendereli (egyszer/oldal).
 */
export function useInvoiceActions(t: TFunction, onDone?: () => void) {
  const [kind, setKind] = useState<ActionKind | null>(null);
  const [target, setTarget] = useState<InvoiceActionRow | null>(null);
  const [stornoComment, setStornoComment] = useState('');
  /**
   * A KIÁLLÍTÁS három dátuma, a kiállítás napjára igazítva. A kolléga felülírhatja — amit itt lát, az megy
   * ki. A számítás a KÖZÖS `shiftIssueDates` (a BFF ugyanezt futtatja falként), tehát az ablak nem egy
   * második szabályt mutat, hanem ugyanannak az előnézetét.
   */
  const [issueDates, setIssueDates] = useState<InvoiceDates>(EMPTY_DATES);

  const close = (): void => {
    setKind(null);
    setTarget(null);
    setStornoComment('');
  };

  const lifecycle = useInvoiceLifecycle(t, () => {
    close();
    onDone?.();
  });

  const openIssue = (row: InvoiceActionRow): void => {
    setIssueDates(
      shiftIssueDates(
        {
          issueDate: row.issueDate ?? null,
          fulfillmentDate: row.fulfillmentDate ?? null,
          dueDate: row.dueDate ?? null,
        },
        todayIso(),
      ),
    );
    setTarget(row);
    setKind('issue');
  };
  const openStorno = (row: InvoiceActionRow): void => {
    setStornoComment('');
    setTarget(row);
    setKind('storno');
  };
  const openCancel = (row: InvoiceActionRow): void => {
    setTarget(row);
    setKind('cancel');
  };

  const confirm = (): void => {
    if (!target) return;
    if (kind === 'issue') {
      lifecycle.issue.mutate({
        id: target.id,
        version: target.version,
        issueDate: issueDates.issueDate || undefined,
        fulfillmentDate: issueDates.fulfillmentDate || undefined,
        dueDate: issueDates.dueDate || undefined,
      });
    } else if (kind === 'storno') {
      lifecycle.storno.mutate({
        id: target.id,
        version: target.version,
        stornoComment: stornoComment.trim() === '' ? null : stornoComment.trim(),
      });
    } else if (kind === 'cancel') {
      lifecycle.cancelProforma.mutate({ id: target.id, version: target.version });
    }
  };

  const pending =
    lifecycle.issue.isPending || lifecycle.storno.isPending || lifecycle.cancelProforma.isPending;

  return {
    kind,
    stornoComment,
    setStornoComment,
    issueDates,
    setIssueDates,
    close,
    confirm,
    pending,
    openIssue,
    openStorno,
    openCancel,
    downloadPdf: lifecycle.downloadPdf,
    pdfPendingId: lifecycle.pdfPendingId,
    /** A lista-szintű státusz-szinkron mutációja (a toolbar „Szinkronizálás" gombja). */
    sync: lifecycle.sync,
  };
}

export type InvoiceActionsController = ReturnType<typeof useInvoiceActions>;

/** A három megerősítő-dialógus (egyszer renderelve az oldalon; a vezérlő állapota nyitja/zárja). */
export function InvoiceActionDialogs({
  controller,
  t,
}: {
  controller: InvoiceActionsController;
  t: TFunction;
}): JSX.Element {
  const { kind, pending, confirm, close, stornoComment, setStornoComment } = controller;
  const { issueDates, setIssueDates } = controller;
  return (
    <>
      <ConfirmDialog
        open={kind === 'issue'}
        onOpenChange={(open) => !open && close()}
        tone="warning"
        title={t('invoices.actions.issueTitle')}
        description={
          <div className="space-y-3">
            <p>{t('invoices.actions.issueDesc')}</p>
            {/* A HÁROM DÁTUM a kiállítás napjára igazítva, ELŐRE kitöltve — felülírható.
                MIÉRT ITT: a piszkozat kelte a generálás hónapjának 2-a (a fűzés hónap-jelölője), tehát egy
                később kiküldött bizonylat különben múltbeli kelttel és lejárt határidővel menne ki. */}
            <div className="grid gap-3 sm:grid-cols-3">
              <DatePicker
                value={issueDates.issueDate}
                onChange={(v) => setIssueDates({ ...issueDates, issueDate: v })}
                label={t('invoices.form.issueDate')}
              />
              <DatePicker
                value={issueDates.fulfillmentDate}
                onChange={(v) => setIssueDates({ ...issueDates, fulfillmentDate: v })}
                label={t('invoices.form.fulfillmentDate')}
              />
              <DatePicker
                value={issueDates.dueDate}
                onChange={(v) => setIssueDates({ ...issueDates, dueDate: v })}
                label={t('invoices.form.dueDate')}
              />
            </div>
          </div>
        }
        confirmLabel={t('invoices.actions.issue')}
        cancelLabel={t('invoices.cancel')}
        pendingLabel={t('invoices.actions.issuing')}
        pending={pending}
        onConfirm={confirm}
      />
      <ConfirmDialog
        open={kind === 'storno'}
        onOpenChange={(open) => !open && close()}
        tone="danger"
        title={t('invoices.actions.stornoTitle')}
        description={
          <div className="space-y-3">
            <p>{t('invoices.actions.stornoDesc')}</p>
            <Textarea
              value={stornoComment}
              onChange={(e) => setStornoComment(e.target.value)}
              placeholder={t('invoices.actions.stornoCommentPlaceholder')}
              rows={3}
              maxLength={1000}
              aria-label={t('invoices.actions.stornoComment')}
            />
          </div>
        }
        confirmLabel={t('invoices.actions.storno')}
        cancelLabel={t('invoices.cancel')}
        pendingLabel={t('invoices.actions.stornoing')}
        pending={pending}
        onConfirm={confirm}
      />
      <ConfirmDialog
        open={kind === 'cancel'}
        onOpenChange={(open) => !open && close()}
        tone="danger"
        title={t('invoices.actions.cancelTitle')}
        description={t('invoices.actions.cancelDesc')}
        confirmLabel={t('invoices.actions.cancel')}
        cancelLabel={t('invoices.cancel')}
        pendingLabel={t('invoices.actions.cancelling')}
        pending={pending}
        onConfirm={confirm}
      />
    </>
  );
}
